import { section, number, equation, table, example, quantitative } from './shared.ts';
export const batch2Math = [
  quantitative(
    'math-detectors',
    'math',
    'Four detectors',
    [
      section('concept', 'Repeated independent trials', [
        'A trial has two categories of outcome, success and failure. Success is just a label: in a defect calculation it might mean finding a faulty component. Independence means an outcome on one trial does not change the probability on another. A fixed number n of independent trials with a fixed success probability p defines a binomial model.',
        'For one specified ordered sequence containing k successes and n − k failures, multiply the individual probabilities. This gives pᵏ × (1 − p)ⁿ⁻ᵏ. It is the probability of that exact order, not yet the probability of every order with k successes. There are C(n,k), read “n choose k,” ways to select the successful positions. Multiply by that count to include all orders.',
      ]),
      section('method', 'Count arrangements or subtract the complement', [
        'To derive the count, first choose k distinct positions in order: n choices, then n − 1, continuing until k positions have been selected. Each unordered selection appears k! times, where k! means k × (k − 1) × … × 1. Divide by that repetition count. For two chosen positions among five, the ordered count is 5 × 4 = 20, but every pair is counted twice, so there are ten pairs. This explains why a binomial coefficient counts positions without overcounting their order.',
        equation(
          'Choose two positions among five',
          'C(5,2)',
          '(5 × 4) / (2 × 1) = 10',
          'The denominator removes the two possible orders of each pair.',
        ),
        equation(
          'Binomial probability',
          'P(exactly k)',
          'C(n,k) × pᵏ × (1 − p)ⁿ⁻ᵏ',
          'The count chooses positions; the product weights each sequence.',
        ),
        'For at least two successes, it is often shorter to subtract zero and one success from the full probability of one. Those excluded cases are disjoint, so their probabilities can be added without double counting.',
        example(
          'Three independent deliveries',
          'Suppose each delivery is late with probability 1/2. The eight late/on-time sequences are equally likely. Exactly two are late in LLO, LOL and OLL, giving 3/8.',
          table(
            'Check every count',
            ['Late deliveries', 'Sequences', 'Probability'],
            [
              ['0', '1', '1/8'],
              ['1', '3', '3/8'],
              ['2', '3', '3/8'],
              ['3', '1', '1/8'],
            ],
          ),
          'Counts total eight and probabilities total one. If lateness had probability other than one half, the sequences would not all have equal probability; their probability weights would matter.',
        ),
        'The independence assumption cannot be inferred merely from using separate devices. Shared weather or a common power failure could correlate their results. Here independence is supplied explicitly as part of the mathematical experiment.',
      ]),
      section(
        'application',
        'Exclude zero and one detection',
        [
          'With four detectors and p = 0.2, zero detections has probability 0.8⁴ = 0.4096. Exactly one has four possible positions, each with probability 0.2 × 0.8³, so its total is also 0.4096.',
          equation(
            'The desired probability',
            'P(at least 2)',
            '1 − 0.4096 − 0.4096 = 0.1808',
            'Remove the two excluded counts.',
          ),
          'As an independent check, exactly two, three and four detections have probabilities 0.1536, 0.0256 and 0.0016. Their sum is the same result. Multiplying 0.2 by four instead gives the expected number of detections, not the probability of the requested event. An expectation has units of detections, whereas this answer is a dimensionless probability between zero and one.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b2-detectors',
      'For a particular weak signal, each of four detectors independently registers it with probability 0.2. What is the probability that at least two detectors register the signal? Give a probability between 0 and 1.',
      0.1808,
      'probability',
      'Subtract zero and one detections: 1 − 0.8⁴ − 4(0.2)(0.8³) = 0.1808.',
      ['concept', 'method', 'application'],
    ),
  ),
  quantitative(
    'math-uploads',
    'math',
    'Upload attempts',
    [
      section('concept', 'Expectation counts possible outcomes', [
        'An expectation is a probability-weighted average of a numerical outcome across repetitions of a specified experiment. It need not be a possible result of one repetition. A mean of 2.5 attempts can describe a procedure that always uses an integer number of attempts.',
        'One calculation lists every possible count and multiplies it by its probability. Another counts attempt slots individually. Each slot contributes one if reached and zero otherwise, so its expected contribution is the probability of reaching it. Adding these contributions counts each realized attempt once.',
      ]),
      section('method', 'Weight each attempt by whether it occurs', [
        equation(
          'Count reached slots',
          'E(attempts)',
          'P(slot 1 occurs) + P(slot 2 occurs) + …',
          'An unused slot contributes zero; a used slot contributes one.',
        ),
        'This identity does not require the slot indicators to be independent. They are dependent: reaching attempt three implies reaching attempt two. Independence of the success outcomes is a different assumption, used to calculate reach probabilities. With success probability p, reaching attempt k requires k − 1 failures and has probability (1 − p)ᵏ⁻¹.',
        example(
          'At most three coin flips',
          'Flip a fair coin until heads or until three flips have been used. Reach probabilities are 1, 1/2 and 1/4. Adding gives 1.75 flips.',
          table(
            'The same expectation by outcome',
            ['Flips used', 'Probability', 'Weighted contribution'],
            [
              ['1', '1/2', '1/2'],
              ['2', '1/4', '1/2'],
              ['3', '1/4', '3/4'],
            ],
          ),
          'The last row includes both TTH and TTT. Success is not required for the final attempt to count. This is why the probability of using all three attempts differs from the probability of first succeeding on attempt three.',
        ),
      ]),
      section(
        'application',
        'Four attempts at most',
        [
          'Each failure has probability 3/4. Attempts one through four are reached with probabilities 1, 3/4, 9/16 and 27/64. Their sum is 175/64, or 2.734375. The last attempt counts whether or not it succeeds.',
          'For a second calculation, the probabilities of using exactly one, two, three and four attempts are 1/4, 3/16, 9/64 and 27/64. They sum to one. Weighting by 1, 2, 3 and 4 gives the same expectation. The procedure has a positive chance of stopping before four, so its mean must be strictly below four.',
          'The unlimited-wait formula 1/p would give four, but that formula describes retries with no cap. A cap changes the experiment, not merely the formatting of the answer. If success probabilities varied across retries, the reached-slot method would still work, but the failure products would use each attempt’s own probability.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b2-uploads',
      'An upload succeeds independently on each attempt with probability 1/4. Retry until the first success, but stop after four attempts even if all fail. What is the expected number of attempts actually made? Give an exact fraction or decimal.',
      175 / 64,
      'attempts',
      'Attempt reach probabilities are 1, 3/4, 9/16 and 27/64; they sum to 175/64.',
      ['concept', 'method', 'application'],
    ),
  ),
  quantitative(
    'math-roundtrip',
    'math',
    'A return journey',
    [
      section('concept', 'Average speed uses total time', [
        'Speed measures distance per unit time. Average speed for an entire journey is total distance divided by total elapsed time. It is not generally the arithmetic mean of the speeds listed in a question. That shortcut works for equal time intervals, because each speed then receives the same time weight.',
        'For equal distances, the slower leg takes longer. The lower speed therefore contributes for more of the total journey time and pulls the overall average below the arithmetic mean. Understanding that weighting makes the formula a consequence of what is being measured, rather than an arbitrary rule.',
      ]),
      section('method', 'Recover time before averaging', [
        equation(
          'Time from distance and speed',
          'time',
          'distance / speed',
          'Kilometers divided by kilometers per hour gives hours.',
        ),
        example(
          'A walking route',
          'A walker covers 6 km at 3 km/h and returns 6 km at 6 km/h. The legs take two hours and one hour. Twelve kilometers in three hours gives 4 km/h.',
          table(
            'Time supplies the weights',
            ['Leg', 'Distance', 'Speed', 'Time'],
            [
              ['Outbound', '6 km', '3 km/h', '2 h'],
              ['Return', '6 km', '6 km/h', '1 h'],
            ],
          ),
          'The time-weighted calculation agrees: (3 × 2 + 6 × 1)/3 = 4. The slower speed has twice the weight because it lasts twice as long.',
        ),
        'For equal distances d at positive speeds a and b, total distance is 2d and total time is d/a + d/b. Canceling d gives 2ab/(a + b), called the harmonic mean. This derivation also shows the restriction: the formula is for equal distances. For unequal distances, return to the actual time and distance totals instead of applying it mechanically.',
      ]),
      section(
        'application',
        'Combine both legs',
        [
          'The daily outbound leg takes 120/60 = 2 hours and the return takes 120/40 = 3 hours. Total distance is 240 km in five hours, so the average speed is 48 km/h. The answer lies between the two speeds and is closer to the slower one, as expected from the unequal time weights.',
          'Fifty km/h would be correct for equal travel times at 60 and 40, but those times would produce unequal distances. A rest stop would add time without distance and lower the overall average further. The prompt excludes stops so travel time and elapsed time coincide.',
          'The same method handles any number of legs: add the distances and divide by the sum of their individual travel times. If speeds are supplied in mixed units, convert first. Treating a kilometer-per-hour value as a meter-per-second value would corrupt both the units and the resulting time weight.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b2-roundtrip',
      'A train travels 120 km at a constant 60 km/h and returns over the same 120 km at a constant 40 km/h. Ignore stops and acceleration. What is its average speed for the entire journey?',
      48,
      'km/h',
      'The legs take two and three hours; 240 km divided by five hours is 48 km/h.',
      ['concept', 'method', 'application'],
    ),
  ),
  quantitative(
    'math-decay',
    'math',
    'Two batches',
    [
      section('concept', 'Equal fractions in equal intervals', [
        'Exponential decay removes the same fraction of the remaining amount over equal time intervals. A half-life is the interval over which that amount halves. One half-life leaves one half; two leave one quarter; three leave one eighth. Absolute losses become smaller because each acts on a smaller remaining amount.',
        'The idealized model assumes a constant decay rate and no other source of removal. The measured quantity is undecayed substance. Decay products may still be physically present, but are not counted as undecayed substance. This prevents interpreting the model as a claim that all physical mass simply disappears.',
      ]),
      section('method', 'Each addition has its own age', [
        equation(
          'Decay of one batch',
          'amount remaining',
          'initial amount × (1/2)ᵏ',
          'k is the number of half-life intervals for that batch.',
        ),
        'When substance is added at different times, each addition has a different age at the final observation. Calculate what survives from each batch, then add. Mixing does not reset the clock for older substance or retrospectively expose new substance to time before it was added.',
        example(
          'Two additions of dye',
          'A dye loses half its active molecules every four hours. Start with 24 units at noon and add 8 fresh units at 16:00. At 20:00, the old batch has experienced two half-lives and contributes 6. The newer batch has experienced one and contributes 4.',
          table(
            'Keep an age ledger',
            ['Batch', 'Initial units', 'Age', 'Units left'],
            [
              ['Noon', '24', '8 hours', '6'],
              ['16:00', '8', '4 hours', '4'],
              ['Total', '32', 'Different ages', '10'],
            ],
          ),
          'Treating all 32 as eight hours old undercounts the newer batch. Treating all as four hours old overcounts the older one. The ledger separates physical age from the time when batches were mixed.',
        ),
      ]),
      section(
        'application',
        'Combine the surviving quantities',
        [
          'The first daily batch has eighteen hours to decay, or three six-hour half-lives: 80 → 40 → 20 → 10. The twenty units added at hour twelve have only one half-life before hour eighteen, leaving ten. Together the remaining undecayed substance is twenty units.',
          'A second calculation starts at hour twelve. Twenty units remain from the original batch, and twenty fresh units are added, making forty. One further half-life reduces that combined amount to twenty. This agrees with the separate-batch calculation.',
          'The total amount added was one hundred, but applying three halvings to all one hundred incorrectly treats the later addition as present at hour zero. Likewise, subtracting a fixed forty units every six hours would eventually produce negative quantities. Fractional decay never does: its remaining amount stays nonnegative and approaches zero as more intervals elapse.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b2-decay',
      'An idealized substance has a constant half-life of six hours. Start with 80 units of undecayed substance at hour 0 and add 20 fresh units at hour 12. Nothing is otherwise added or removed. How many units of undecayed substance remain at hour 18, counting both batches but excluding decay products?',
      20,
      'units',
      'The old batch contributes 80/8 = 10 and the later batch contributes 20/2 = 10.',
      ['concept', 'method', 'application'],
    ),
  ),
  quantitative(
    'math-river',
    'math',
    'A riverside enclosure',
    [
      section('concept', 'Describe all feasible designs', [
        'Optimization asks for the greatest or least achievable value subject to constraints. A feasible design obeys those constraints; an optimal design is at least as good as every other feasible one. Finding one attractive design does not prove optimality. We need a representation that compares the entire allowed family.',
        'Rectangle area is length times width: the length counts unit squares in a row and the width counts rows. Fencing adds only the side lengths actually fenced. Along a river, one side needs no fence, so the constraint involves three sides rather than the full perimeter.',
      ]),
      section('method', 'Use the constraint to eliminate a variable', [
        'Let x be each perpendicular side and y the parallel side that needs fencing. With F meters of fence, 2x + y = F, giving y = F − 2x. Area is then x(F − 2x). A positive rectangle requires x between zero and F/2.',
        equation(
          'Write area around its peak',
          'A',
          'F²/8 − 2(x − F/4)²',
          'Expanding the square gives Fx − 2x², the same area expression.',
        ),
        'A square is nonnegative. Subtracting twice that square can only reduce area, so the maximum is F²/8, attained when x = F/4 and y = F/2. This is a proof over every feasible width, without calculus.',
        example(
          'Forty meters of fence',
          'With F = 40, use perpendicular sides of 10 meters and a parallel side of 20 meters, for 200 square meters.',
          table(
            'Nearby feasible designs',
            ['x', '40 − 2x', 'Area'],
            [
              ['8 m', '24 m', '192 m²'],
              ['10 m', '20 m', '200 m²'],
              ['12 m', '16 m', '192 m²'],
            ],
          ),
          'The table illustrates the peak, but the completed-square expression proves it. Checking a few rows alone cannot exclude a better width somewhere between them.',
        ),
      ]),
      section(
        'application',
        'Use the 120-meter constraint',
        [
          'The maximizing perpendicular side is 120/4 = 30 meters. The parallel fenced side is 60 meters. The fence check is 30 + 60 + 30 = 120, and the area is 30 × 60 = 1,800 square meters. All dimensions are positive.',
          'A square is optimal for a different problem with fixed perimeter and all four sides fenced. Here the free riverside changes the balance, so that familiar answer cannot simply be reused. As x approaches zero or sixty, one rectangle dimension approaches zero and so does its area, consistent with an interior maximum.',
          'If available river frontage or land depth imposed another bound, the candidate would need to satisfy it. The prompt supplies no such restriction. The physical quantities also distinguish the result from a perimeter: fence has units of meters, while area has units of square meters.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b2-river',
      'A rectangular enclosure lies beside a straight river, so its riverside needs no fence. You have 120 meters of fence for the other three sides. There are no limits on frontage or land area and no gate gaps. What is the largest area you can enclose?',
      1800,
      'square meters',
      'Sides 30, 60 and 30 use the fence. A = 120x − 2x² = 1800 − 2(x − 30)² proves the maximum.',
      ['concept', 'method', 'application'],
    ),
  ),
];
